import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api, extractApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth-context";
import { canCreateOrEditCRM, canSeeArchivedCRM } from "../lib/crm-permissions";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import EmptyState from "../components/empty/EmptyState";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const NONE = "__none__";

export default function ContactsPage() {
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useAuth();
  const perm = { role, isSuperAdmin };
  const canCreate = canCreateOrEditCRM(perm);
  const canSeeArchived = canSeeArchivedCRM(perm);
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    mobile_phone: "",
    position: "",
    department: "",
    linkedin_url: "",
    notes: "",
    company_id: NONE,
    consent_marketing: false,
  });
  const [saving, setSaving] = useState(false);

  const searchRef = useRef(search);
  searchRef.current = search;

  const load = useCallback(async (searchValue = searchRef.current) => {
    setLoading(true);
    try {
      const params = {};
      if (searchValue) params.search = searchValue;
      if (canSeeArchived && showArchived) params.include_archived = true;
      const [r, c] = await Promise.all([
        api.get("/api/contacts", { params }),
        api.get("/api/companies"),
      ]);
      setRows(r.data);
      setCompanies(c.data);
    } catch (e) {
      toast.error(extractApiError(e, "Kontakte konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, [canSeeArchived, showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("Bitte Vor- und Nachname angeben.");
    setSaving(true);
    try {
      const payload = { ...form, company_id: form.company_id === NONE ? null : form.company_id };
      await api.post("/api/contacts", payload);
      toast.success("Kontakt erstellt.");
      setForm({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        mobile_phone: "",
        position: "",
        department: "",
        linkedin_url: "",
        notes: "",
        company_id: NONE,
        consent_marketing: false,
      });
      setDialog(false);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Kontakt konnte nicht erstellt werden."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Kontakte"
        description="Ansprechpartner und Personen verwalten."
        actions={
          canCreate ? (
            <Button onClick={() => setDialog(true)} data-testid="contact-create-button">
              <Plus className="w-4 h-4 mr-1.5" /> Kontakt erstellen
            </Button>
          ) : null
        }
      />

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="surface rounded-xl p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="contacts-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche nach Name, E-Mail, Position, Abteilung …"
            className="pl-10"
          />
        </div>
        {canSeeArchived ? (
          <div className="mt-3 flex items-center justify-end">
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Archivierte anzeigen
            </label>
          </div>
        ) : null}
      </form>

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Wird geladen …</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Noch keine Kontakte"
          description="Legen Sie Ihren ersten Kontakt an."
          actionLabel={canCreate ? "Kontakt erstellen" : undefined}
          onAction={canCreate ? () => setDialog(true) : undefined}
          testId="contacts-empty"
        />
      ) : (
        <div data-testid="contacts-table" className="obsidian overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border">
                <tr className="text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Firma</th>
                  <th className="px-4 py-3 font-medium">E-Mail</th>
                  <th className="px-4 py-3 font-medium">Abteilung</th>
                  <th className="px-4 py-3 font-medium">Position</th>
                  <th className="px-4 py-3 font-medium">Telefon</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/kontakte/${c.id}`)} className="hover:bg-muted/50 cursor-pointer transition-colors" data-testid={`contacts-table-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium">
                      <span className="inline-flex items-center gap-2">
                        {c.first_name} {c.last_name}
                        {c.archived_at ? <Badge variant="secondary">Archiviert</Badge> : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {companies.find((co) => co.id === c.company_id)?.name || "—"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.department || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.position || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Kontakt erstellen</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Fld label="Vorname *"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="contact-form-first-name" /></Fld>
            <Fld label="Nachname *"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="contact-form-last-name" /></Fld>
            <Fld label="E-Mail"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Fld>
            <Fld label="Telefon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Fld>
            <Fld label="Mobil"><Input value={form.mobile_phone} onChange={(e) => setForm({ ...form, mobile_phone: e.target.value })} /></Fld>
            <Fld label="Position"><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} /></Fld>
            <Fld label="Abteilung"><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Fld>
            <Fld label="Firma">
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger><SelectValue placeholder="Keine Firma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Keine Firma</SelectItem>
                  {companies.map((co) => <SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <Fld label="LinkedIn" full><Input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} /></Fld>
            <Fld label="Notizen" full><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Fld>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setDialog(false)}>Abbrechen</Button>
              <Button type="submit" disabled={saving} data-testid="contact-form-submit">
                {saving ? "Speichern …" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fld({ label, children, full = false }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
